import { ArrowLeft, Banknote, CheckCheck, Pencil, Send } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { accedeModule } from '@/auth/modules';
import { useSession } from '@/auth/session';
import { toast } from 'sonner';
import { controlesDe } from '@/api/client';
import { useActionsFed, useFed } from '@/api/hooks';
import { LIBELLE_NATURE, type DecisionApi } from '@/api/types';
import { BoutonPdf, Chargement, CircuitTimeline, ConfirmationDialog, ControlesListe, DecisionDialog, ErreurBloc, EtapeBadge, Historique, PageHeader, PaiementBadge, StatutBadge } from '@/components/common';
import { PiecesJointes } from '@/components/pieces-jointes';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { BandeauCorrection } from '@/components/documents';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateFr, dateHeureFr, montant } from '@/lib/utils';

export function FedDetail() {
  const { id } = useParams();
  const session = useSession();
  const u = session!.utilisateur;
  const { data, isLoading, error } = useFed(id);
  const actions = useActionsFed();
  const [dialogue, setDialogue] = useState<'decision' | 'soumettre' | 'resoumettre' | 'liquider' | 'payer' | null>(null);

  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const { fed, circuit, actions: a } = data;
  const erreur = (e: unknown) => {
    const c = controlesDe(e);
    toast.error(e instanceof Error ? e.message : 'Action impossible.', { description: c.length ? c.map((x) => x.message).join(' · ') : undefined });
  };
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
      setDialogue(null);
    } catch (e) {
      erreur(e);
    }
  };
  const decider = (decision: DecisionApi, motif?: string) => executer(() => actions.decider.mutateAsync({ id: fed.id, decision, motif }), decision === 'VALIDER' && a.etape === 'DIRECTEUR_GENERAL' ? 'FED autorisé : engagement budgétaire créé.' : 'Décision enregistrée.');

  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to="/fed" className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Engagements
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {fed.numero ?? 'FED (brouillon)'} <StatutBadge statut={fed.statut} /> {fed.statut === 'SOUMIS' && <EtapeBadge etape={data.etapeCourante} />}
            {fed.statut === 'AUTORISE' && <PaiementBadge etat={fed.etatPaiement} />}
            {fed.urgence && <Badge variant="destructive">Urgent</Badge>}
            {fed.suspendu && <Badge variant="destructive">Suspendu</Badge>}
          </span>
        }
        sousTitre={
          <>
            {fed.objet} · {LIBELLE_NATURE[fed.nature]} · {fed.structure?.libelle} · émis par {fed.emetteur?.prenom} {fed.emetteur?.nom}
            {fed.dateSoumission ? ` · soumis le ${dateHeureFr(fed.dateSoumission)}` : ''}
            {fed.dateAutorisation ? ` · autorisé le ${dateHeureFr(fed.dateAutorisation)}` : ''}
            {fed.datePaiement ? ` · payé le ${dateHeureFr(fed.datePaiement)}` : ''}
          </>
        }
        actions={
          <>
            <BoutonPdf chemin={`/pdf/fed/${fed.id}`} nom={`${fed.numero ?? 'FED'}.pdf`} />
            {a.modifier && (
              <Button variant="outline" asChild>
                <Link to={`/fed/${fed.id}/modifier`}>
                  <Pencil className="size-4" /> Modifier
                </Link>
              </Button>
            )}
            {a.soumettre && (
              <Button onClick={() => setDialogue('soumettre')}>
                <Send className="size-4" /> Soumettre
              </Button>
            )}
            {a.resoumettre && (
              <Button onClick={() => setDialogue('resoumettre')}>
                <Send className="size-4" /> Soumettre à nouveau
              </Button>
            )}
            {a.decider && <Button onClick={() => setDialogue('decision')}>Décider</Button>}
            {a.liquider && (
              <Button variant="secondary" onClick={() => setDialogue('liquider')}>
                <CheckCheck className="size-4" /> Mettre en paiement
              </Button>
            )}
            {a.payer && (
              <Button onClick={() => setDialogue('payer')}>
                <Banknote className="size-4" /> Payer
              </Button>
            )}
          </>
        }
      />
      <BandeauCorrection statut={fed.statut} correction={data.correction} motif={fed.motifAnnulation} automatique={fed.annulationAutomatique} />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border bg-primary/5 p-4">
              <div className="text-xs uppercase text-muted-foreground">Montant</div>
              <div className="num text-xl font-semibold">{montant(fed.montantUsd, true)}</div>
              {fed.devise !== 'USD' && <div className="text-xs text-muted-foreground">{fed.montant.toLocaleString('fr-FR')} {fed.devise}</div>}
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs uppercase text-muted-foreground">Ligne budgétaire</div>
              <div className="font-semibold">{fed.ligneBudgetaire?.code}</div>
              <div className="text-xs text-muted-foreground">{fed.ligneBudgetaire?.libelle}</div>
              {fed.ligneBudgetaire?.id && accedeModule(u.profils, 'plan') && (
                <Link to={`/plan/${fed.ligneBudgetaire.id}`} className="mt-1 block text-xs text-primary hover:underline">
                  Fiche de suivi de la sous-activité
                </Link>
              )}
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs uppercase text-muted-foreground">Paiement</div>
              <div className="font-semibold">{fed.modePaiement ?? '—'}</div>
              <div className="text-xs text-muted-foreground">semaine : {dateFr(fed.semaineDecaissement)}</div>
            </div>
          </div>

          {fed.suspendu && (
            <Alert variant="destructive">
              <AlertTitle>FED suspendu</AlertTitle>
              <AlertDescription>Une note explicative à incidence budgétaire est en cours sur le TDR : aucune décision n'est possible jusqu'à la décision finale sur la note.</AlertDescription>
            </Alert>
          )}
          {data.bonCommande && (
            <Alert>
              <AlertTitle>Facture du bon de commande {data.bonCommande.numero}</AlertTitle>
              <AlertDescription>
                <Link to={`/achats/${data.bonCommande.id}`} className="text-primary hover:underline">
                  {data.bonCommande.objet}
                </Link>{' '}
                · {data.bonCommande.fournisseur} · bon de {montant(data.bonCommande.montantUsd, true)}
              </AlertDescription>
            </Alert>
          )}
          {data.budget && (
            <Alert variant={data.budget.ok ? 'default' : 'destructive'}>
              <AlertTitle>{data.budget.ok ? (data.budget.previsionnelOk ? 'Disponibilité budgétaire confirmée' : 'Disponible, mais pas après les dossiers en circuit') : 'Dépassement budgétaire'}</AlertTitle>
              <AlertDescription>
                {data.budget.message} Disponible {montant(data.budget.disponible)} · prévisionnel {montant(data.budget.disponiblePrevisionnel)}.
              </AlertDescription>
            </Alert>
          )}

          {data.controles.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Contrôles avant soumission</CardTitle>
              </CardHeader>
              <CardContent>
                <ControlesListe controles={data.controles} />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Lignes</CardTitle>
              {data.tdr && (
                <CardDescription>
                  Issu du TDR{' '}
                  <Link to={`/tdr/${data.tdr.id}`} className="text-primary hover:underline">
                    {data.tdr.numero}
                  </Link>{' '}
                  · {data.tdr.titre}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Bénéficiaire</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fed.lignes.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>{l.ordre}</TableCell>
                      <TableCell>{l.beneficiaireType === 'AGENT' ? (l.agent ? `${l.agent.prenom} ${l.agent.nom}` : 'Agent') : l.beneficiaireLibelle}</TableCell>
                      <TableCell>{l.description}</TableCell>
                      <TableCell className="num text-right">{montant(l.montant, true)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} className="text-right font-medium">
                      Total
                    </TableCell>
                    <TableCell className="num text-right font-semibold">{montant(fed.montant, true)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <PiecesJointes objetType="FED" objetId={fed.id} typesSuggeres={fed.nature === 'FOURNISSEUR' ? ['FACTURE', 'PROFORMA', 'BON_COMMANDE', 'CONTRAT', 'AUTRE'] : ['LISTE_PRESENCE', 'RAPPORT', 'JUSTIFICATIF', 'AUTRE']} description={fed.nature === 'FOURNISSEUR' ? 'Paiement extérieur : facture ou proforma et bon de commande ou contrat obligatoires avant la soumission.' : 'Facultatives, selon la nature de la dépense.'} />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Circuit de validation</CardTitle>
              {circuit && <CardDescription>Sans visa du DGA Opérations · {circuit.steps.length} étapes</CardDescription>}
            </CardHeader>
            <CardContent>
              <CircuitTimeline circuit={circuit} />
            </CardContent>
          </Card>
          {circuit && circuit.history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Historique</CardTitle>
              </CardHeader>
              <CardContent>
                <Historique circuit={circuit} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <DecisionDialog ouvert={dialogue === 'decision'} onOpenChange={(o) => setDialogue(o ? 'decision' : null)} decisions={a.decisions} etape={a.etape} onConfirmer={decider} chargement={actions.decider.isPending} />
      <ConfirmationDialog ouvert={dialogue === 'soumettre'} onOpenChange={(o) => setDialogue(o ? 'soumettre' : null)} titre="Soumettre le FED" description="Un numéro sera attribué et le circuit de validation ouvert." libelle="Soumettre" onConfirmer={() => executer(() => actions.soumettre.mutateAsync(fed.id), 'FED soumis.')} chargement={actions.soumettre.isPending} />
      <ConfirmationDialog ouvert={dialogue === 'resoumettre'} onOpenChange={(o) => setDialogue(o ? 'resoumettre' : null)} titre="Soumettre à nouveau" libelle="Soumettre à nouveau" onConfirmer={(r) => executer(() => actions.resoumettre.mutateAsync({ id: fed.id, reponse: r || undefined }), 'FED soumis à nouveau.')} chargement={actions.resoumettre.isPending} champ={{ label: 'Réponse au validateur' }} />
      <ConfirmationDialog ouvert={dialogue === 'liquider'} onOpenChange={(o) => setDialogue(o ? 'liquider' : null)} titre="Mettre en paiement" description="Service fait : le montant passe en liquidation sur la ligne budgétaire." libelle="Mettre en paiement" onConfirmer={(r) => executer(() => actions.liquider.mutateAsync({ id: fed.id, reference: r || undefined }), 'FED mis en paiement.')} chargement={actions.liquider.isPending} champ={{ label: 'Référence (facultatif)', lignes: 1 }} />
      <ConfirmationDialog ouvert={dialogue === 'payer'} onOpenChange={(o) => setDialogue(o ? 'payer' : null)} titre="Confirmer le paiement" description="Le décaissement est enregistré sur la ligne budgétaire." libelle="Payer" onConfirmer={(r) => executer(() => actions.payer.mutateAsync({ id: fed.id, reference: r || undefined }), 'Paiement enregistré.')} chargement={actions.payer.isPending} champ={{ label: 'Référence bancaire ou de caisse', lignes: 1 }} />
    </div>
  );
}
